(() => {
  if (customElements.get('qr-kit-showcase')) return;
  const clamp = value => Math.min(1, Math.max(0, value));
  const ease = value => 1 - Math.pow(1 - clamp(value), 3);
  const between = (progress, from, to) => ease((progress - from) / (to - from));
  const mix = (from, to, amount) => from + (to - from) * amount;
  const stops = [.18, .44, .75, 1];
  const sceneRatio = 5 / 4;
  const rotations = {monitor:-3, laptop:4, guide:-10, answer:-2, picture:9};

  class QRKitShowcase extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const {signal} = this.abort;
      this.generation = (this.generation ?? 0) + 1;
      this.film = this.querySelector('[data-kit-film]');
      this.camera = this.querySelector('[data-kit-camera]');
      this.scene = this.querySelector('[data-kit-scene]');
      this.stamp = this.querySelector('[data-kit-stamp]');
      this.stampImage = this.querySelector('[data-kit-stamp-image]');
      this.stampSeen = false;
      this.stampSuppressed = false;
      if (!this.film || !this.camera || !this.scene) return;
      this.pieces = [...this.querySelectorAll('[data-kit-object]')].filter(piece => piece.dataset.kitObject in rotations);
      this.chapters = [...this.querySelectorAll('[data-kit-chapter]')];
      this.panels = [...this.querySelectorAll('[data-kit-panel]')];
      this.words = [...this.querySelectorAll('.qr-kit-story-word')];
      this.wordFitSignature = null;
      this.textMetrics = null;
      this.controls = this.querySelector('[data-kit-chapters]');
      this.status = this.querySelector('[data-kit-status]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = matchMedia('(min-width: 990px)');
      this.header = document.querySelector('#header-component');
      this.banner = document.querySelector('[data-qr-seasonal-banner]');
      this.assetsReady = false;
      this.controlsReady = false;
      this.layoutReadyFrames = 0;
      this.layoutReadySignature = null;
      this.warming = false;
      this.failed = false;
      this.frame = null;
      this.pendingAlignment = null;
      this.stage = -1;
      this.progress = this.reduced.matches ? 1 : stops[0];
      this.manualProgress = this.progress;
      this.userSelected = false;
      this.skipWhileLoading = false;
      this.controls.hidden = false;
      this.controls.setAttribute('aria-busy','true');
      this.chapters.forEach(button => { button.disabled = true; });
      if (this.stampImage) {
        const showArt = () => { if (this.stampImage.naturalWidth > 0) this.stamp.classList.add('has-art'); };
        this.stampImage.addEventListener('load',showArt,{signal});
        this.stampImage.addEventListener('error',()=>this.stamp.classList.remove('has-art'),{signal});
        if (this.stampImage.complete) showArt();
      }
      this.classList.add('is-enhanced');
      this.pieces.forEach(piece => piece.style.setProperty('--final-rotation', rotations[piece.dataset.kitObject] + 'deg'));
      this.render(this.progress);
      this.schedule = () => {
        if (!this.frame && !signal.aborted) this.frame = requestAnimationFrame(() => this.update());
      };
      const generation = this.generation;
      const refitWords = () => {
        if (!this.isConnected || signal.aborted || generation !== this.generation) return;
        this.wordFitSignature = null;
        this.schedule();
      };
      document.fonts?.addEventListener('loadingdone', refitWords, {signal});
      document.fonts?.ready.then(refitWords);
      window.addEventListener('scroll', this.schedule, {passive:true, capture:true, signal});
      const cancelAlignment = () => this.cancelAlignment();
      const changeLayout = () => { this.cancelAlignment(); this.schedule(); };
      for (const type of ['wheel','touchstart','pointerdown']) window.addEventListener(type, cancelAlignment, {passive:true, capture:true, signal});
      window.addEventListener('keydown', cancelAlignment, {capture:true, signal});
      window.addEventListener('focusin', event => { if (!this.controls.contains(event.target)) this.cancelAlignment(); }, {capture:true, signal});
      window.addEventListener('resize', changeLayout, {passive:true, signal});
      this.reduced.addEventListener('change', changeLayout, {signal});
      this.desktop.addEventListener('change', changeLayout, {signal});
      this.chapters.forEach(button => button.addEventListener('click', () => this.selectChapter(Number(button.dataset.kitChapter), true), {signal}));
      this.controls.addEventListener('keydown', event => {
        if (event.ctrlKey || event.metaKey || event.altKey || !this.assetsReady || !this.controlsReady || this.failed) return;
        const button = event.target.closest('[data-kit-chapter]');
        if (!button) return;
        const index = this.chapters.indexOf(button);
        const next = event.key === 'ArrowRight' ? (index + 1) % 4 : event.key === 'ArrowLeft' ? (index + 3) % 4 : event.key === 'Home' ? 0 : event.key === 'End' ? 3 : null;
        if (next === null) return;
        event.preventDefault();
        this.chapters[next].focus({preventScroll:true});
        this.selectChapter(next, true);
      }, {signal});
      this.querySelector('.qr-kit-skip')?.addEventListener('click', event => {
        const target = document.getElementById(event.currentTarget.getAttribute('href').slice(1));
        if (!target) return;
        event.preventDefault();
        this.cancelAlignment();
        this.stampSuppressed = true;
        this.stamp?.classList.remove('is-stamping');
        if (!this.controlsReady && !this.enhanced) this.skipWhileLoading = true;
        this.manualProgress = 1;
        this.progress = 1;
        this.render(1);
        target.scrollIntoView({block:'start', behavior:'instant'});
        target.focus({preventScroll:true});
      }, {signal});
      this.resizeObserver = new ResizeObserver(this.schedule);
      [this, this.scene, this.header, this.banner].filter(Boolean).forEach(element => this.resizeObserver.observe(element));
      this.headerObserver = new MutationObserver(this.schedule);
      if (this.banner) this.headerObserver.observe(this.banner, {attributes:true, attributeFilter:['hidden','data-season']});
      this.intersectionObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.warm();
      }, {root:this.scrollRoot(), rootMargin:'400px'});
      this.intersectionObserver.observe(this.film);
      this.schedule();
    }

    scrollRoot() {
      return this.desktop.matches ? document.querySelector('.page-wrapper') : null;
    }

    measure() {
      const root = this.scrollRoot();
      const rootRect = root?.getBoundingClientRect();
      const bannerBottom = this.banner && !this.banner.hidden ? Math.max(0, this.banner.getBoundingClientRect().bottom) : 0;
      const viewportTop = rootRect?.top ?? bannerBottom;
      const viewportHeight = root?.clientHeight ?? innerHeight - viewportTop;
      const headerHeight = this.header?.getBoundingClientRect().height ?? 0;
      const localTop = Math.ceil(headerHeight + 14);
      this.top = viewportTop + localTop;
      this.height = Math.min(760, viewportHeight - localTop - 24);
      this.distance = Math.round(Math.min(1000, Math.max(620, viewportHeight * 1.05)));
      this.enhanced = this.assetsReady && !this.skipWhileLoading && !this.failed && !this.reduced.matches && this.desktop.matches && this.getBoundingClientRect().width >= 900 && this.height >= 580;
      this.classList.toggle('is-converging', this.enhanced);
      this.dataset.mode = this.enhanced ? 'cinematic' : 'manual';
      const compactHeight = !this.enhanced && this.height < 540;
      this.dataset.compactHeight = compactHeight;
      if (!this.enhanced) {
        const phone = innerWidth < 750;
        let overhead = phone ? (compactHeight ? 342 : 390) : 230;
        if (phone && typeof getComputedStyle === 'function') {
          const head = this.querySelector('.qr-kit-film-head');
          const caption = this.querySelector('.qr-kit-scene-caption');
          const story = this.querySelector('[data-kit-story]');
          const stage = this.querySelector('.qr-kit-stage');
          if (head && caption && story && stage) {
            const number = value => Number.parseFloat(value) || 0;
            const outerHeight = element => {
              const styles = getComputedStyle(element);
              return element.getBoundingClientRect().height + number(styles.marginTop) + number(styles.marginBottom);
            };
            const cameraStyles = getComputedStyle(this.camera);
            const stageStyles = getComputedStyle(stage);
            overhead = outerHeight(head) + outerHeight(this.controls) + outerHeight(caption) + outerHeight(story)
              + number(cameraStyles.paddingTop) + number(cameraStyles.paddingBottom) + number(stageStyles.paddingTop) + number(stageStyles.paddingBottom);
          }
        }
        const width = Math.max(120, Math.floor((this.height - overhead) * sceneRatio));
        const value = width + 'px';
        if (this.style.getPropertyValue('--qr-manual-scene-width') !== value) this.style.setProperty('--qr-manual-scene-width', value);
      }
      if (this.enhanced) {
        this.style.setProperty('--qr-film-top', localTop + 'px');
        this.style.setProperty('--qr-film-height', this.height + 'px');
        this.style.setProperty('--qr-film-distance', this.distance + 'px');
        this.style.setProperty('--qr-scene-width', Math.max(300, Math.floor((this.height - 224) * sceneRatio)) + 'px');
      }
      this.fitChapterWord();
      this.scale = this.scene.clientWidth / 1200;
      if (this.reduced.matches && !this.userSelected) this.manualProgress = 1;
      return {root, viewportTop, viewportBottom:viewportTop + viewportHeight};
    }

    fitChapterWord() {
      const word = this.words?.[this.stage];
      const panel = this.panels?.[this.stage];
      if (!word || !panel || !panel.clientWidth || typeof getComputedStyle !== 'function') return;
      const text = word.textContent.trim();
      const signature = [this.stage,this.dataset.mode,this.dataset.compactHeight,innerWidth,panel.clientWidth,text].join(':');
      if (signature === this.wordFitSignature) return;
      this.textMetrics ??= document.createElement('canvas').getContext('2d');
      if (!this.textMetrics) return;
      word.style.removeProperty('--qr-kit-word-size');
      let style = getComputedStyle(word);
      const baseSize = Number.parseFloat(style.fontSize);
      const available = Math.max(1, word.clientWidth - (Number.parseFloat(style.paddingLeft) || 0) - (Number.parseFloat(style.paddingRight) || 0) - 2);
      if (!baseSize) return;
      const widthAt = computed => {
        this.textMetrics.font = `${computed.fontStyle || 'normal'} ${computed.fontWeight || '400'} ${computed.fontSize} ${computed.fontFamily}`;
        return this.textMetrics.measureText(text).width + (Number.parseFloat(computed.letterSpacing) || 0) * Math.max(0,text.length - 1);
      };
      let measured = widthAt(style);
      let size = Math.min(baseSize, Math.floor(baseSize * available / Math.max(1,measured)));
      size = Math.max(1,size);
      word.style.setProperty('--qr-kit-word-size',size + 'px');
      style = getComputedStyle(word);
      measured = widthAt(style);
      if (measured > available) {
        size = Math.max(1,Math.floor(size * available / measured));
        word.style.setProperty('--qr-kit-word-size',size + 'px');
      }
      this.wordFitSignature = signature;
    }

    async warm() {
      if (this.warming || this.assetsReady || this.failed) return;
      this.warming = true;
      const generation = this.generation;
      const images = [...this.querySelectorAll('[data-kit-objects] img')];
      images.forEach(image => { image.loading = 'eager'; });
      if (this.stampImage) this.stampImage.loading = 'eager';
      try {
        await Promise.all(images.map(image => image.decode()));
        if (!this.isConnected || this.abort?.signal.aborted || generation !== this.generation) return;
        this.assetsReady = true;
        this.scene.classList.add('is-ready');
        this.schedule();
      } catch {
        if (!this.isConnected || this.abort?.signal.aborted || generation !== this.generation) return;
        this.failed = true;
        this.cancelAlignment();
        this.controlsReady = false;
        this.chapters.forEach(button => { button.disabled = true; });
        this.controls.removeAttribute('aria-busy');
        this.controls.hidden = true;
        this.manualProgress = 1;
        this.progress = 1;
        this.classList.remove('is-converging');
        this.render(1);
      }
    }

    selectChapter(index, announce = false) {
      if (!Number.isInteger(index) || index < 0 || index > 3 || this.failed || !this.assetsReady || !this.controlsReady) return;
      this.cancelAlignment();
      this.userSelected = true;
      this.measure();
      const progress = stops[index];
      this.manualProgress = progress;
      if (this.enhanced) {
        const root = this.scrollRoot();
        const travel = (progress - stops[0]) / (1 - stops[0]);
        const offset = this.film.getBoundingClientRect().top - this.top + travel * this.distance;
        const scrollTop = root?.scrollTop ?? document.scrollingElement?.scrollTop ?? window.scrollY;
        (root ?? window).scrollTo({top:Math.max(0, scrollTop + offset), behavior:'instant'});
      } else {
        this.alignManualCamera();
        this.pendingAlignment = {stage:index, generation:this.generation, frames:0, stableFrames:0, corrections:0, deadline:performance.now() + 1200};
      }
      if (announce && this.status) this.status.textContent = this.chapters[index].dataset.kitLabel;
      this.schedule();
    }

    cancelAlignment() {
      this.pendingAlignment = null;
    }

    alignManualCamera() {
      const root = this.scrollRoot();
      const offset = this.camera.getBoundingClientRect().top - this.top;
      if (Math.abs(offset) <= 2) return false;
      const scrollTop = root?.scrollTop ?? document.scrollingElement?.scrollTop ?? window.scrollY;
      (root ?? window).scrollTo({top:Math.max(0, scrollTop + offset), behavior:'instant'});
      return true;
    }

    settleManualAlignment(target) {
      const pending = this.pendingAlignment;
      if (!pending) return;
      pending.frames += 1;
      if (pending.generation !== this.generation || this.enhanced || this.failed || pending.frames >= 45 || performance.now() > pending.deadline) {
        this.cancelAlignment();
        return;
      }
      if (this.progress === target && this.stage === pending.stage) {
        this.measure();
        if (this.enhanced) { this.cancelAlignment(); return; }
        if (this.alignManualCamera()) {
          pending.stableFrames = 0;
          pending.corrections += 1;
          if (pending.corrections >= 3) { this.cancelAlignment(); return; }
        } else {
          pending.stableFrames += 1;
          if (pending.stableFrames >= 3) { this.cancelAlignment(); return; }
        }
      } else {
        pending.stableFrames = 0;
      }
      this.schedule();
    }

    enableSettledControls(rect) {
      if (!this.assetsReady || this.failed || this.controlsReady) return;
      const signature = [this.dataset.mode, Math.round(rect.height), this.scene.clientWidth, Math.round(this.camera.getBoundingClientRect().height), Math.round(this.top)].join(':');
      this.layoutReadyFrames = signature === this.layoutReadySignature ? this.layoutReadyFrames + 1 : 1;
      this.layoutReadySignature = signature;
      if (this.layoutReadyFrames >= 2) {
        this.controlsReady = true;
        this.controls.removeAttribute('aria-busy');
        this.chapters.forEach(button => { button.disabled = false; });
        if (this.progress >= .999 && !this.stampSeen) this.schedule();
      } else {
        this.schedule();
      }
    }

    update() {
      this.frame = null;
      if (!this.abort || this.abort.signal.aborted) return;
      const geometry = this.measure();
      const rect = this.film.getBoundingClientRect();
      if (!this.assetsReady && !this.failed && rect.top < geometry.viewportBottom + 400 && rect.bottom > geometry.viewportTop - 400) this.warm();
      const target = this.enhanced ? stops[0] + clamp((this.top - rect.top) / this.distance) * (1 - stops[0]) : this.manualProgress;
      const animate = !this.reduced.matches && this.assetsReady && !this.failed;
      const next = animate ? mix(this.progress, target, .24) : target;
      this.progress = Math.abs(next - target) < .001 ? target : next;
      this.render(this.progress);
      this.settleManualAlignment(target);
      this.enableSettledControls(rect);
      if (animate && this.progress !== target && rect.bottom > geometry.viewportTop && rect.top < geometry.viewportBottom) this.schedule();
    }

    renderStamp(progress) {
      if (!this.stamp) return;
      const visible = progress >= .999;
      this.stamp.dataset.visible = String(visible);
      this.stamp.setAttribute('aria-hidden',String(!visible));
      if (!visible) {
        this.stamp.classList.remove('is-stamping');
        return;
      }
      if (this.stampSeen) return;
      if (this.reduced.matches || this.failed || this.stampSuppressed) {
        this.stampSeen = true;
        return;
      }
      if (!this.assetsReady || !this.controlsReady) return;
      const rect = this.stamp.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= innerHeight) return;
      this.stampSeen = true;
      this.stamp.classList.add('is-stamping');
    }

    render(progress) {
      const settle = between(progress, .78, 1);
      const monitor = between(progress, 0, .18);
      const score = between(progress, .23, .44);
      const host = between(progress, .48, .68);
      const answer = between(progress, .55, .73);
      const picture = between(progress, .61, .78);
      const poses = {
        monitor:[mix(-280, 180, monitor) * (1 - score), mix(80, 24, monitor) * (1 - score), mix(-16, -3, monitor), mix(.8, 1.27, monitor) - score * .27, monitor],
        laptop:[mix(350, 0, score), mix(110, 0, score), mix(18, 4, score), mix(.78, 1.12, score) - settle * .12, score],
        guide:[mix(-190, 0, host), mix(135, 0, host), mix(-28, -10, host), mix(.82, 1.08, host) - settle * .08, host],
        answer:[0, mix(210, 0, answer), mix(16, -2, answer), mix(.82, 1.04, answer) - settle * .04, answer],
        picture:[mix(220, 0, picture), mix(150, 0, picture), mix(26, 9, picture), mix(.82, 1.04, picture) - settle * .04, picture],
      };
      const scale = this.scale ?? this.scene.clientWidth / 1200;
      this.pieces.forEach(piece => {
        const [x,y,rotation,size,opacity] = poses[piece.dataset.kitObject];
        piece.style.setProperty('--move-x', (x * scale).toFixed(2) + 'px');
        piece.style.setProperty('--move-y', (y * scale).toFixed(2) + 'px');
        piece.style.setProperty('--rotation', rotation.toFixed(2) + 'deg');
        piece.style.setProperty('--kit-scale', size.toFixed(4));
        piece.style.setProperty('--kit-opacity', opacity.toFixed(4));
        piece.style.setProperty('--kit-shadow-y', (mix(26, 10, opacity) * (1 - settle * .15)).toFixed(2) + 'px');
        piece.style.setProperty('--kit-shadow-blur', mix(20, 12, opacity).toFixed(2) + 'px');
      });
      const stage = progress < .25 ? 0 : progress < .49 ? 1 : progress < .84 ? 2 : 3;
      if (stage !== this.stage) {
        this.stage = stage;
        this.wordFitSignature = null;
        this.panels.forEach((panel, index) => { panel.hidden = index !== stage; });
        this.chapters.forEach((button, index) => { if (index === stage) button.setAttribute('aria-current','step'); else button.removeAttribute('aria-current'); });
      }
      this.fitChapterWord();
      this.chapters.forEach((button, index) => button.style.setProperty('--chapter-progress', clamp(progress * 4 - index).toFixed(3)));
      this.scene.dataset.stage = stage;
      this.scene.dataset.complete = progress >= .999;
      this.scene.dataset.progress = progress.toFixed(4);
      this.scene.classList.toggle('is-in-flight', progress > 0 && progress < 1);
      this.renderStamp(progress);
    }

    disconnectedCallback() {
      this.controlsReady = false;
      this.chapters?.forEach(button => { button.disabled = true; });
      this.cancelAlignment();
      this.abort?.abort();
      this.abort = null;
      this.generation = (this.generation ?? 0) + 1;
      this.resizeObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.textMetrics = null;
      this.wordFitSignature = null;
      this.stamp?.classList.remove('is-stamping');
      this.classList.remove('is-converging');
    }
  }
  customElements.define('qr-kit-showcase', QRKitShowcase);
})();
